import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { SharedArticle, SharedCollection, SharedExtract, LinkAnalytics } from '../types/sharing';

interface DeepLinkResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

/**
 * Hook for fetching shared article by token
 */
export function useSharedArticle(token: string | undefined): DeepLinkResult<SharedArticle> {
  const [data, setData] = useState<SharedArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('No share token provided');
      return;
    }

    async function fetchSharedArticle() {
      try {
        setLoading(true);
        setError(null);

        // Fetch the share token and associated article
        const { data: shareData, error: shareError } = await supabase
          .from('share_tokens')
          .select(`
            *,
            articles (
              id,
              title,
              content,
              url,
              excerpt,
              word_count,
              reading_time,
              saved_at,
              highlights
            ),
            profiles (
              display_name,
              avatar_url
            )
          `)
          .eq('token', token)
          .eq('type', 'article')
          .single();

        if (shareError) throw new Error('Share link not found or expired');

        // Check expiration
        if (shareData.expires_at && new Date(shareData.expires_at) < new Date()) {
          throw new Error('This share link has expired');
        }

        // Track the view (token is guaranteed to be defined after the early return check)
        await trackLinkEvent(token!, 'view');

        // Increment view count
        await supabase
          .from('share_tokens')
          .update({ view_count: (shareData.view_count || 0) + 1 })
          .eq('token', token);

        const article = shareData.articles;
        setData({
          id: article.id,
          title: shareData.settings?.customTitle || article.title,
          content: article.content,
          url: article.url,
          excerpt: shareData.settings?.customDescription || article.excerpt,
          wordCount: article.word_count,
          readingTime: article.reading_time,
          savedAt: article.saved_at,
          sharedBy: shareData.settings?.showAuthor ? {
            name: shareData.profiles?.display_name || 'Anonymous',
            avatar: shareData.profiles?.avatar_url
          } : undefined,
          highlights: shareData.settings?.allowHighlights ? article.highlights : undefined
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load shared article');
      } finally {
        setLoading(false);
      }
    }

    fetchSharedArticle();
  }, [token]);

  return { data, loading, error };
}

/**
 * Hook for fetching shared collection by token
 * Uses the direct share_token field on collections table
 */
export function useSharedCollection(token: string | undefined): DeepLinkResult<SharedCollection> {
  const [data, setData] = useState<SharedCollection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('No share token provided');
      return;
    }

    async function fetchSharedCollection() {
      try {
        setLoading(true);
        setError(null);

        // Fetch collection directly by share_token
        const { data: collection, error: collectionError } = await supabase
          .from('collections')
          .select(`
            id,
            name,
            description,
            color,
            is_public,
            shared_at,
            user_id,
            profiles!collections_user_id_fkey (
              display_name,
              avatar_url
            )
          `)
          .eq('share_token', token)
          .eq('is_public', true)
          .single();

        if (collectionError || !collection) {
          throw new Error('Collection not found or is private');
        }

        // Fetch articles in the collection
        const { data: articleCollections } = await supabase
          .from('article_collections')
          .select('article_id')
          .eq('collection_id', collection.id);

        const articleIds = (articleCollections || []).map(ac => ac.article_id);

        let articles: Array<{
          id: string;
          title: string;
          excerpt: string;
          url: string;
          wordCount: number;
        }> = [];

        if (articleIds.length > 0) {
          const { data: articlesData } = await supabase
            .from('articles')
            .select('id, title, excerpt, url, word_count')
            .in('id', articleIds);

          articles = (articlesData || []).map(a => ({
            id: a.id,
            title: a.title || 'Untitled',
            excerpt: a.excerpt || '',
            url: a.url,
            wordCount: a.word_count || 0
          }));
        }

        // Track view (silently fail if table doesn't exist)
        try {
          await trackLinkEvent(token!, 'view');
        } catch {
          // Silently ignore
        }

        const profile = Array.isArray(collection.profiles)
          ? collection.profiles[0]
          : collection.profiles;

        setData({
          id: collection.id,
          name: collection.name,
          description: collection.description,
          articleCount: articles.length,
          articles,
          sharedBy: profile ? {
            name: profile.display_name || 'Anonymous',
            avatar: profile.avatar_url
          } : undefined
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load shared collection');
      } finally {
        setLoading(false);
      }
    }

    fetchSharedCollection();
  }, [token]);

  return { data, loading, error };
}

/**
 * Hook for fetching shared extract (highlight) by token
 */
export function useSharedExtract(token: string | undefined): DeepLinkResult<SharedExtract> {
  const [data, setData] = useState<SharedExtract | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('No share token provided');
      return;
    }

    async function fetchSharedExtract() {
      try {
        setLoading(true);
        setError(null);

        const { data: shareData, error: shareError } = await supabase
          .from('share_tokens')
          .select(`
            *,
            profiles (
              display_name,
              avatar_url
            )
          `)
          .eq('token', token)
          .eq('type', 'extract')
          .single();

        if (shareError) throw new Error('Share link not found or expired');

        if (shareData.expires_at && new Date(shareData.expires_at) < new Date()) {
          throw new Error('This share link has expired');
        }

        await trackLinkEvent(token!, 'view');

        await supabase
          .from('share_tokens')
          .update({ view_count: (shareData.view_count || 0) + 1 })
          .eq('token', token);

        setData({
          id: shareData.content_id,
          text: shareData.extract_text,
          note: shareData.settings?.allowNotes ? shareData.extract_note : undefined,
          articleTitle: shareData.article_title,
          articleUrl: shareData.article_url,
          sharedBy: shareData.settings?.showAuthor ? {
            name: shareData.profiles?.display_name || 'Anonymous',
            avatar: shareData.profiles?.avatar_url
          } : undefined
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load shared extract');
      } finally {
        setLoading(false);
      }
    }

    fetchSharedExtract();
  }, [token]);

  return { data, loading, error };
}

/**
 * Track link analytics event
 */
async function trackLinkEvent(token: string, event: LinkAnalytics['event']) {
  try {
    await supabase.from('link_analytics').insert({
      token,
      event,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString()
    });
  } catch {
    // Silently fail analytics - don't break the user experience
  }
}

/**
 * Hook for handling deep link redirect after auth
 */
export function useDeepLinkRedirect() {
  const navigate = useNavigate();
  const location = useLocation();

  const saveIntendedDestination = useCallback(() => {
    const path = location.pathname + location.search + location.hash;
    if (path !== '/' && path !== '/app') {
      sessionStorage.setItem('deep_link_redirect', path);
    }
  }, [location]);

  const getIntendedDestination = useCallback((): string | null => {
    return sessionStorage.getItem('deep_link_redirect');
  }, []);

  const clearIntendedDestination = useCallback(() => {
    sessionStorage.removeItem('deep_link_redirect');
  }, []);

  const redirectToIntendedDestination = useCallback(() => {
    const destination = getIntendedDestination();
    if (destination) {
      clearIntendedDestination();
      navigate(destination, { replace: true });
      return true;
    }
    return false;
  }, [navigate, getIntendedDestination, clearIntendedDestination]);

  return {
    saveIntendedDestination,
    getIntendedDestination,
    clearIntendedDestination,
    redirectToIntendedDestination
  };
}

/**
 * Generate a share token for content
 */
export async function createShareToken(
  type: 'article' | 'collection' | 'extract',
  contentId: string,
  settings: {
    allowHighlights?: boolean;
    allowNotes?: boolean;
    showAuthor?: boolean;
    customTitle?: string;
    customDescription?: string;
    expiresIn?: number; // days
    extractText?: string;
    extractNote?: string;
    articleTitle?: string;
    articleUrl?: string;
  } = {}
): Promise<{ token: string; url: string } | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Must be logged in to share');

    // Generate a short, URL-safe token
    const token = generateShortToken();

    const expiresAt = settings.expiresIn
      ? new Date(Date.now() + settings.expiresIn * 24 * 60 * 60 * 1000).toISOString()
      : null;

    const { error } = await supabase.from('share_tokens').insert({
      token,
      type,
      content_id: contentId,
      created_by: user.id,
      expires_at: expiresAt,
      settings: {
        allowHighlights: settings.allowHighlights ?? true,
        allowNotes: settings.allowNotes ?? false,
        showAuthor: settings.showAuthor ?? true,
        customTitle: settings.customTitle,
        customDescription: settings.customDescription
      },
      extract_text: settings.extractText,
      extract_note: settings.extractNote,
      article_title: settings.articleTitle,
      article_url: settings.articleUrl
    });

    if (error) throw error;

    const prefix = type === 'article' ? 's' : type === 'collection' ? 'c' : 'e';
    const url = `${window.location.origin}/${prefix}/${token}`;

    return { token, url };
  } catch (err) {
    console.error('Failed to create share token:', err);
    return null;
  }
}

/**
 * Generate a short, URL-safe token
 */
function generateShortToken(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let token = '';
  const array = new Uint8Array(8);
  crypto.getRandomValues(array);
  for (let i = 0; i < 8; i++) {
    token += chars[array[i] % chars.length];
  }
  return token;
}
